import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Waves, Check, ChevronDown, ChevronUp, MapPin, ArrowRight, Phone, Factory, UtensilsCrossed } from 'lucide-react';
import BeforeAfterSection from '../components/BeforeAfterSection';

const CommercialHighPressureCleaning = ({ onQuoteClick }) => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      q: "What surfaces can you high-pressure clean?",
      a: "Building exteriors, walkways, driveways, carparks, loading docks, concrete warehouse floors and outdoor areas across commercial and industrial sites."
    },
    {
      q: "Do you offer ongoing high-pressure cleaning or only one-off jobs?",
      a: "Both. We provide ongoing exterior high-pressure cleaning for operating sites, including regular work for KFC, Domino's and The Cray locations across WA covering walkways, drive-throughs, kitchen floors and windows, as well as one-off jobs for warehouse turnovers and other properties."
    },
    {
      q: "Can you clean multi-storey building exteriors?",
      a: "Yes. For buildings beyond safe reach from ground level, we hire scissor lifts suited to the site, giving us the flexibility to take on multi-storey properties."
    },
    {
      q: "Do you work with schools, pubs and government buildings?",
      a: "Yes. Alongside our ongoing commercial work, we're focused on bringing high-pressure cleaning to schools, pubs, taverns and government buildings across WA."
    }
  ];

  // Service Schema for SEO
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Commercial High-Pressure Cleaning",
    "provider": {
      "@type": "LocalBusiness",
      "@id": "https://www.extreme-clean.com.au/#organization",
      "name": "Extreme Clean Australia"
    },
    "areaServed": [
      "Perth", "Osborne Park", "Malaga", "Kewdale", "Welshpool", "Canning Vale", 
      "Bibra Lake", "Jandakot", "Wangara", "Balcatta", "Bayswater", "Rockingham", 
      "Mandurah", "Joondalup", "Midland", "Yanchep", "Leederville", "Northbridge", 
      "Mount Lawley", "Fremantle", "Cockburn", "Landsdale", "Hope Valley", 
      "Kalgoorlie", "Esperance", "Albany", "Busselton", "Bunbury", "Margaret River", "Australind", "Geraldton"
    ],
    "description": "From building exteriors to driveways, carparks and warehouse floors, high-pressure cleaning restores surfaces that a standard clean can't touch. Extreme Clean Australia provides commercial high-pressure cleaning across WA, including ongoing exterior cleaning for KFC, Domino's and The Cray locations statewide, with scissor lift access available for taller buildings."
  };

  const relatedServices = [
    {
      title: 'Warehouse & Industrial Cleaning',
      description: 'High-pressure cleaning as part of complete warehouse end-of-lease turnovers.',
      link: '/services/warehouse-industrial-cleaning-perth',
      icon: Factory
    },
    {
      title: 'Commercial Kitchen Cleaning',
      description: 'Kitchen floor rotary and extraction cleaning alongside exterior high-pressure work.',
      link: '/services/commercial-kitchen-cleaning-perth',
      icon: UtensilsCrossed
    }
  ];

  return (
    <>
      <Helmet>
        <link rel="canonical" href="https://www.extreme-clean.com.au/services/commercial-high-pressure-cleaning-perth" />
        <title>Commercial High-Pressure Cleaning Perth | Extreme Clean Australia</title>
        <meta name="description" content="High-pressure cleaning for schools, pubs, taverns, government buildings and industrial sites across Perth and WA. Scissor lift access for multi-storey buildings. 20+ years experience." />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />

      <div className="pt-24 lg:pt-44" data-testid="commercial-high-pressure-page">
        {/* Hero - Full width background style */}
        <section className="bg-[#0A0A0A] py-24 relative" data-testid="service-hero">
          <div className="absolute inset-0">
            <img 
              src="https://images.unsplash.com/photo-1718152423993-a29048dbc223?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHxwb3dlciUyMHdhc2hpbmclMjBwYXZlbWVudCUyMHdhdGVyJTIwc3ByYXl8ZW58MHx8fHwxNzg5MzUzNzExfDA&ixlib=rb-4.1.0&q=85"
              alt="Worker using high-pressure cleaner on commercial pavement"
              className="w-full h-full object-cover opacity-30"
            />
          </div>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 bg-[#3B82F6] bg-opacity-20 px-4 py-2 mb-6">
                <Waves size={20} className="text-[#3B82F6]" />
                <span className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider">High-Pressure Cleaning</span>
              </div>
              <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl !leading-[1.2] mb-6 text-white">
                Commercial High-Pressure Cleaning Perth
              </h1>
              <p className="text-xl text-gray-300 mb-4">
                High-pressure cleaning for schools, pubs, taverns, government buildings and industrial sites
              </p>
              <p className="text-gray-400 mb-8">
                From building exteriors to driveways, carparks and warehouse floors, high-pressure cleaning restores surfaces that a standard clean can't touch. Extreme Clean Australia provides commercial high-pressure cleaning across WA, including ongoing exterior cleaning for KFC, Domino's and The Cray locations statewide, with scissor lift access available for taller buildings.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={onQuoteClick}
                  className="bg-[#3B82F6] hover:bg-blue-600 text-white uppercase font-bold tracking-widest px-8 py-4 text-sm transition-all flex items-center justify-center gap-2"
                >
                  Get Free Quote <ArrowRight size={18} />
                </button>
                <a
                  href="tel:0861875523"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#0A0A0A] transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm flex items-center justify-center gap-2"
                >
                  <Phone size={18} /> Call 08 6187 5523
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Scope</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">What's Included</h2>
              <p className="text-gray-600 mb-8">
                High-pressure cleaning for exterior building surfaces, walkways, driveways, carparks, loading docks and outdoor areas across commercial and industrial sites. For food-service clients including KFC, Domino's and The Cray, this extends to drive-through cleaning, kitchen floor rotary and extraction cleaning, and window cleaning. We also handle one-off jobs as part of warehouse end-of-lease turnovers, and hire scissor lifts for taller buildings. Extreme Clean Australia also cleans playground equipment, including McDonald's PlayPlaces.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  'Building exteriors',
                  'Walkways & driveways',
                  'Carparks & loading docks',
                  'Concrete warehouse floors',
                  'Drive-through cleaning',
                  'Scissor lift access for height',
                  'Playground equipment'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <Check size={20} className="text-[#3B82F6] flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Before and After */}
        <BeforeAfterSection
          title="Before and After"
          sectionId="high-pressure-before-after"
          pairs={[
            {
              images: [
                {
                  src: '/images/before-after/paving-before.jpg',
                  alt: 'Commercial paving with dirt and staining before high-pressure cleaning',
                  label: 'Before'
                },
                {
                  src: '/images/before-after/paving-after.jpg',
                  alt: 'Commercial paving restored to clean surface after high-pressure cleaning',
                  label: 'After'
                }
              ]
            }
          ]}
        />

        {/* Focus Areas */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Focus Areas</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">Schools, Pubs, Taverns and Government Buildings</h2>
              <p className="text-gray-600 mb-8">
                Alongside our ongoing commercial work, we're focused on bringing high-pressure cleaning to schools, pubs, taverns and government buildings across WA, sites where exterior presentation and safety both matter, and where the job needs to be done properly, not rushed.
              </p>
              <div className="flex flex-wrap gap-3">
                {['Schools', 'Pubs', 'Taverns', 'Government Buildings', 'Industrial Sites', 'Food-Service Locations'].map((area, index) => (
                  <span key={index} className="bg-white border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700">
                    {area}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Working at Heights */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Access</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">Working at Heights</h2>
              <p className="text-gray-600">
                Not every job is ground-level. For building exteriors beyond safe reach from the ground, we hire scissor lifts suited to the site, giving us the flexibility to take on multi-storey properties as well as standard ground-level work.
              </p>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 bg-[#273268]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Why Us</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6 text-white">Why Businesses Choose Extreme Clean Australia</h2>
              <p className="text-gray-300">
                High-pressure cleaning needs the right equipment and the judgement to use it properly, too much pressure or the wrong technique can damage a surface rather than clean it. Backed by 20+ years across commercial, food-service and industrial sites in WA, including ongoing exterior work for KFC, Domino's and The Cray, we bring real experience to every job, not just equipment. We're especially active across Perth's industrial areas, including Osborne Park, Wangara, Malaga, Welshpool, Landsdale and Hope Valley.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">FAQ</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-8">Common Questions</h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div 
                    key={index} 
                    className="border border-gray-200 bg-gray-50"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="w-full px-6 py-4 flex items-center justify-between text-left"
                      aria-expanded={openFaq === index}
                    >
                      <span className="font-heading font-bold text-lg pr-4">{faq.q}</span>
                      {openFaq === index ? (
                        <ChevronUp size={20} className="text-[#3B82F6] flex-shrink-0" />
                      ) : (
                        <ChevronDown size={20} className="text-gray-400 flex-shrink-0" />
                      )}
                    </button>
                    <div 
                      className={`overflow-hidden transition-all duration-300 ${openFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
                    >
                      <div className="px-6 pb-4">
                        <p className="text-gray-600">{faq.a}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Areas We Service */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-2">
                <MapPin size={20} className="text-[#3B82F6]" />
                <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider">Service Areas</p>
              </div>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">Areas We Service</h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="font-heading font-bold text-xl mb-3">Perth Metro</h3>
                  <p className="text-gray-600">
                    We service the entire Perth metro area, from Yanchep in the north to Mandurah in the south, including Osborne Park, Malaga, Kewdale, Welshpool, Canning Vale, Bibra Lake, Jandakot, Wangara, Balcatta, Bayswater, Rockingham, Joondalup and Midland.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-heading font-bold text-xl mb-3">Regional WA</h3>
                  <p className="text-gray-600">
                    Kalgoorlie, Esperance, Albany, Busselton, Bunbury, Margaret River, Australind, Geraldton.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Related Services */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Related</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-8">Related Services</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {relatedServices.map((service, index) => (
                  <Link 
                    key={index}
                    to={service.link}
                    className="block bg-gray-50 p-6 border border-gray-200 hover:border-[#3B82F6] hover:shadow-md transition-all group"
                  >
                    <div className="w-12 h-12 bg-blue-50 mb-4 flex items-center justify-center group-hover:bg-[#3B82F6] transition-colors">
                      <service.icon size={24} className="text-[#3B82F6] group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="font-heading font-bold text-lg mb-2 group-hover:text-[#3B82F6] transition-colors">{service.title}</h3>
                    <p className="text-gray-600 text-sm">{service.description}</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-[#3B82F6]">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-6">
              Ready to Discuss Your High-Pressure Cleaning Needs?
            </h2>
            <p className="text-blue-100 text-lg mb-8">
              Get a free quote for commercial high-pressure cleaning, from driveways to multi-storey building exteriors.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={onQuoteClick}
                className="bg-white text-[#0A0A0A] hover:bg-gray-100 transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm"
              >
                Get Free Quote
              </button>
              <a
                href="tel:0861875523"
                className="border-2 border-white text-white hover:bg-white hover:text-[#3B82F6] transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm flex items-center justify-center gap-2"
              >
                <Phone size={18} /> Call 08 6187 5523
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default CommercialHighPressureCleaning;
